import { FC, FormEvent } from "react";
import { useController, useForm } from "react-hook-form";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { FormLayout, Label, Larger } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import Button from "@/components/Button";
import { productionColors } from "@/utils/data/production";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import FormField from "@/components/Forms/FormField";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { HighlightResource } from "@/types/resources";

type FormDataT = {
  title: string;
  value: string;
  color: string;
  is_mandatory: boolean;
};

const ColorRadios = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ColorRadioLabel = styled.label`
  border-radius: 4px;
  padding: 3px;
  display: block;
  cursor: pointer;
  border: 1px solid #f2f3f7;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.3);
  }
`;

const ColorRadioLabelInner = styled.div<{ $background: string }>`
  background: ${(props) => props.$background};
  width: 66px;
  height: 32px;
  border-radius: 4px;
`;

const ColorInput = styled.input`
  display: none;
  &:checked {
    + ${ColorRadioLabel} {
      border-color: rgba(34, 34, 34, 0.3);
    }
  }
`;

interface Props {
  onClose: () => void;
  highlightsColors?: string[];
  highlight?: HighlightResource;
}

const HighlightsModal: FC<Props> = ({
  onClose,
  highlightsColors,
  highlight,
}) => {
  const { teamId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const filteredColors = productionColors.filter(
    (color) => !highlightsColors?.includes(color) || color === highlight?.color
  );

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: highlight?.title ?? "",
      value: highlight?.value.toString(),
      color: highlight?.color ?? "",
      is_mandatory: !!highlight?.isMandatory,
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ ...data }) => {
      try {
        const body = {
          ...data,
          team_id: teamId,
        };

        if (highlight) {
          await api.put<never, { id: string }>(
            `/v1/production/highlights/${highlight.id}`,
            body
          );
        } else {
          await api.post<never, HighlightResource>(
            "/v1/production/highlights",
            body
          );
        }

        queryClient.invalidateQueries(["script_highlights", { teamId }]);

        alerts.success(
          `Highlight has been successfully ${
            !!highlight ? "updated" : "created"
          }`
        );

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    name: "color",
    control,
  });

  return (
    <Modal>
      <SidePanel heading="Create Category" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="title"
                control={control}
                label="Category name"
              />
              <FormField<FormDataT>
                name="value"
                control={control}
                label="Value"
                type="select"
                options={Array.from({ length: 12 }, (_, i) => ({
                  value: (i + 1).toString(),
                  label: (i + 1).toString(),
                }))}
              />
              <FormField<FormDataT>
                name="is_mandatory"
                control={control}
                type="switch"
                headline="Mandatory highlight"
                description="This highlight will be shown as required in the highlights table score."
              />
              <Larger>
                <Label>Assign color</Label>
                <ColorRadios aria-label="color">
                  {filteredColors.map((color, index) => (
                    <div key={index}>
                      <ColorInput
                        type="radio"
                        id={`color-${color}`}
                        name="color"
                        onChange={(e) => onChange(e.target.value)}
                        checked={value === color}
                        value={color}
                      />
                      <ColorRadioLabel htmlFor={`color-${color}`}>
                        <ColorRadioLabelInner $background={color} />
                      </ColorRadioLabel>
                    </div>
                  ))}
                </ColorRadios>
                {!!error && (
                  <ValidationMessage>{error.message}</ValidationMessage>
                )}
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default HighlightsModal;
